import React, { useState, useEffect, useRef } from 'react';
import { fixHtmlImagePaths, processEmbedUrls } from "../utils/imageUtils";
import { useParams, Link } from 'react-router-dom';
import RecruitmentService from '../services/RecruitmentService';
import { normalizeImageUrl } from '../utils/imageUtils';
import { useData } from '../context/DataContext';
import { updateMetaTags } from '../utils/seoUtils';
import useTikTokEmbed from '../utils/useTikTokEmbed';

const RecruitmentDetailPage = () => {
    const { slug } = useParams();
    const { settings } = useData();
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [relatedJobs, setRelatedJobs] = useState([]);

    const lastFetchedSlugRef = useRef(null);
    const isFetchingRef = useRef(false);
    const contentRef = useRef(null);

    useTikTokEmbed(contentRef, [job]);

    useEffect(() => {
        if (job) {
            updateMetaTags({
                title: job.title,
                description: job.description || job.title,
                image: job.imageUrl,
            });
        }
    }, [job]);

    useEffect(() => {
        const fetchJobDetail = async () => {
            if (isFetchingRef.current) return;
            if (lastFetchedSlugRef.current === slug) return;
            
            isFetchingRef.current = true;
            setLoading(true);
            try {
                const [jobData, allJobs] = await Promise.all([
                    RecruitmentService.getJobBySlug(slug),
                    RecruitmentService.getAllJobs()
                ]);
                
                setJob(jobData);
                lastFetchedSlugRef.current = slug;

                if (Array.isArray(allJobs)) {
                    setRelatedJobs(allJobs
                        .filter(j => (
                            j.slug !== slug &&
                            (j._id || j.id) !== (jobData?._id || jobData?.id)
                        ))
                        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                        .slice(0, 8)
                    );
                }
            } catch (error) {
                console.error("Lỗi tải chi tiết tuyển dụng:", error);
            } finally {
                setLoading(false);
                isFetchingRef.current = false;
            }
        };

        if (slug) {
            fetchJobDetail();
            window.scrollTo(0, 0);
        }
    }, [slug]);

    if (loading) {
        return <div className="text-center py-5">Đang tải...</div>;
    }

    if (!job) {
        return (
            <div className="text-center py-5">
                <h2>Không tìm thấy tin tuyển dụng</h2>
                <Link to="/tuyen-dung">Quay lại danh sách</Link>
            </div>
        );
    }

    return (
        <main style={{ backgroundColor: '#f4f7f6', color: '#333', paddingBottom: '100px' }}>
            <div className="py-4 mt-[50px]">
                <div className="container px-4">
                    <nav aria-label="breadcrumb">
                        <ol className="flex items-center gap-2 text-xs md:text-sm uppercase tracking-wider mb-0 p-0 list-none">
                            <li className="breadcrumb-item"><Link to="/" className="hover:text-red-600 transition-colors no-underline text-slate-500">Trang chủ</Link></li>
                            <span className="opacity-30">/</span>
                            <li className="breadcrumb-item"><Link to="/tuyen-dung" className="hover:text-red-600 transition-colors no-underline text-slate-500">Tuyển dụng</Link></li>
                            <span className="opacity-30">/</span>
                            <li className="breadcrumb-item active text-slate-900 font-bold" aria-current="page">{job.title}</li>
                        </ol>
                    </nav>
                </div>
            </div>

            <div className="container">
                <div className="row" style={{ display: 'flex', alignItems: 'flex-start' }}>

                    <div className="col-lg-9 col-md-12 mb-4">
                        <div style={{
                            backgroundColor: '#fff',
                            padding: '30px',
                            borderRadius: '8px',
                            boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            maxWidth: '100%'
                        }}>
                            <h1 style={{
                                fontSize: '26px',
                                fontWeight: '800',
                                marginBottom: '20px',
                                color: '#ff0000',
                                lineHeight: '1.4',
                                wordBreak: 'break-word'
                            }}>
                                {job.title}
                            </h1>

                            <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '15px',
                                color: '#666',
                                fontSize: '14px',
                                borderBottom: '1px solid #f0f0f0',
                                paddingBottom: '15px',
                                marginBottom: '25px'
                            }}>
                                <span className="d-flex align-items-center gap-1"><i className="fa fa-map-marker text-danger"></i> {job.location || 'Hà Nội'}</span>
                                <span className="d-flex align-items-center gap-1"><i className="fa fa-briefcase text-primary"></i> {job.type || 'Toàn thời gian'}</span>
                                <span className="d-flex align-items-center gap-1"><i className="fa fa-money text-danger"></i> Lương: <strong style={{ color: '#ff0000' }}>{job.salary || 'Thỏa thuận'}</strong></span>
                            </div>

                            <div className="job-content ql-editor" style={{
                                fontSize: '16px',
                                lineHeight: '1.8',
                                color: '#444',
                                overflow: 'hidden',
                                wordBreak: 'normal',
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word'
                            }}>
                                {job.content ? (
                                    <div
                                        ref={contentRef}
                                        className="recruitment-content-inner"
                                        style={{ maxWidth: '100%', wordBreak: 'normal', overflowWrap: 'break-word', wordWrap: 'break-word' }}
                                        dangerouslySetInnerHTML={{ __html: processEmbedUrls(fixHtmlImagePaths(job.content.replace(/&nbsp;/g, ' '))) }}
                                    />
                                ) : (
                                    <p>{job.description || 'Nội dung tuyển dụng hiện đang được cập nhật...'}</p>
                                )}
                            </div>

                            <div style={{
                                marginTop: '40px',
                                padding: '30px',
                                background: '#f8f9fa',
                                borderRadius: '12px',
                                border: '1px solid #e9ecef',
                                textAlign: 'center'
                            }}>
                                <h4 style={{ fontWeight: 'bold', marginBottom: '15px' }}>Ứng tuyển cho vị trí này?</h4>
                                <div className="d-flex justify-content-center gap-3 flex-wrap">
                                    <a href={`tel:${settings?.hotline?.replace(/[^0-9]/g, '')}`} className="btn btn-outline-dark btn-lg rounded-pill px-5 fw-bold" style={{ border: '2px solid #333' }}>
                                        {settings?.hotline}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-lg-3 d-none d-lg-block" style={{ position: 'sticky', top: '100px' }}>
                        <div style={{ width: '100%' }}>
                            <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', marginBottom: '25px' }}>
                                <h5 style={{ fontSize: '16px', fontWeight: 'bold', borderBottom: '2px solid #ff0000', paddingBottom: '10px', marginBottom: '20px' }}>THÔNG TIN CHUNG</h5>

                                <div className="mb-3">
                                    <small style={{ color: '#999', fontSize: '11px', fontWeight: 'bold', display: 'block', textTransform: 'uppercase' }}>Hạn nộp hồ sơ</small>
                                    <span style={{ color: '#d00', fontWeight: 'bold', fontSize: '15px' }}>
                                        {job.deadline ? new Date(job.deadline).toLocaleDateString('vi-VN') : 'Càng sớm càng tốt'}
                                    </span>
                                </div>

                                <div className="mb-3">
                                    <small style={{ color: '#999', fontSize: '11px', fontWeight: 'bold', display: 'block', textTransform: 'uppercase' }}>Ngày đăng tin</small>
                                    <span style={{ fontWeight: '600' }}>
                                        {job.createdAt ? new Date(job.createdAt).toLocaleDateString('vi-VN') : 'Vừa mới đăng'}
                                    </span>
                                </div>

                                <div className="mb-0">
                                    <small style={{ color: '#999', fontSize: '11px', fontWeight: 'bold', display: 'block', textTransform: 'uppercase' }}>Địa điểm làm việc</small>
                                    <span style={{ fontWeight: '600' }}>{job.location || 'Hà Nội'}</span>
                                </div>
                            </div>

                            {relatedJobs.length > 0 && (
                                <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                                    <h5 style={{ fontSize: '16px', fontWeight: 'bold', borderBottom: '2px solid #ddd', paddingBottom: '10px', marginBottom: '20px' }}>MỚI NHẤT</h5>

                                    {relatedJobs.slice(0, 3).map((rj, idx) => (
                                        <div key={idx} style={{ paddingBottom: '15px', marginBottom: '15px', borderBottom: idx < 2 ? '1px dashed #eee' : 'none' }}>
                                            <Link to={`/tuyen-dung/${rj.slug}`} style={{ color: '#333', textDecoration: 'none', fontWeight: '700', fontSize: '14px', display: 'block', lineHeight: '1.4' }}>
                                                {rj.title}
                                            </Link>
                                            <div className="d-flex justify-content-between align-items-center mt-2" style={{ fontSize: '12px', color: '#888' }}>
                                                <span>{rj.location || 'Hà Nội'}</span>
                                                <span style={{ color: '#ff0000', fontWeight: 'bold' }}>{rj.salary || 'Thỏa thuận'}</span>
                                            </div>
                                        </div>
                                    ))}

                                    <Link to="/tuyen-dung" className="btn btn-light w-100 fw-bold border" style={{ fontSize: '13px' }}>
                                        Tất cả vị trí <i className="fa fa-angle-right ms-1"></i>
                                    </Link>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {relatedJobs.length > 0 && (
                    <div style={{ marginTop: '60px' }}>
                        <h3 style={{
                            fontSize: '22px',
                            fontWeight: '800',
                            marginBottom: '30px',
                            textAlign: 'center',
                            textTransform: 'uppercase',
                            position: 'relative',
                            paddingBottom: '15px'
                        }}>
                            Việc làm tương tự
                            <span style={{
                                content: '""',
                                position: 'absolute',
                                bottom: '0',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: '60px',
                                height: '3px',
                                backgroundColor: '#ff0000'
                            }}></span>
                        </h3>

                        <div className="row">
                            {relatedJobs.map((rj, idx) => (
                                <div key={idx} className="col-lg-3 col-md-6 mb-4">
                                    <div style={{
                                        backgroundColor: '#fff',
                                        borderRadius: '12px',
                                        overflow: 'hidden',
                                        boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                                        height: '100%',
                                        transition: 'transform 0.3s ease'
                                    }} className="course-card-hover">
                                        <Link to={`/tuyen-dung/${rj.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                                            <div style={{ position: 'relative', height: '180px', overflow: 'hidden' }}>
                                                <img
                                                    src={normalizeImageUrl(rj.imageUrl)}
                                                    alt={rj.title}
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                />
                                                <div style={{ position: 'absolute', top: '10px', right: '10px' }}>
                                                    <span className="badge bg-primary rounded-pill px-2 py-1 shadow" style={{ fontSize: '10px' }}>
                                                        {rj.salary || 'Thỏa thuận'}
                                                    </span>
                                                </div>
                                            </div>
                                            <div style={{ padding: '20px', textAlign: 'center' }}>
                                                <h4 style={{
                                                    fontSize: '15px',
                                                    fontWeight: '700',
                                                    lineHeight: '1.5',
                                                    height: '45px',
                                                    overflow: 'hidden',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    marginBottom: '10px'
                                                }}>
                                                    {rj.title}
                                                </h4>
                                                <div className="d-flex justify-content-center gap-3 mt-2" style={{ color: '#888', fontSize: '11px' }}>
                                                    <span><i className="fa fa-map-marker me-1"></i> {rj.location || 'Hà Nội'}</span>
                                                    <span><i className="fa fa-clock-o me-1"></i> {rj.type || 'Toàn thời gian'}</span>
                                                </div>
                                            </div>
                                        </Link>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <style>{`
                .job-content img { max-width: 100% !important; height: auto !important; }
                .job-content iframe { max-width: 100% !important; }
                .job-content table { width: 100% !important; overflow-wrap: break-word; }
                .job-content * { 
                    word-break: normal !important; 
                    overflow-wrap: break-word !important; 
                    word-wrap: break-word !important; 
                }
                
                @keyframes blink-orange {
                    0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255, 0, 0, 0.7); }
                    50% { transform: scale(1.05); box-shadow: 0 0 15px 5px rgba(255, 0, 0, 0.4); }
                    100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255, 0, 0, 0); }
                }
                
                .btn-blink-orange {
                    background-color: #ff0000 !important;
                    border-color: #ff0000 !important;
                    color: #fff !important;
                    animation: blink-orange 1.5s infinite ease-in-out;
                    transition: all 0.3s ease;
                }
                
                .btn-blink-orange:hover {
                    background-color: #e57300 !important;
                    transform: scale(1.1) !important;
                    box-shadow: 0 0 20px rgba(255, 0, 0, 0.6) !important;
                }

                @media (max-width: 991px) {
                    .col-lg-3 { position: static !important; }
                }
                
                .course-card-hover:hover {
                    transform: translateY(-5px);
                }
            `}</style>
        </main>
    );
};

export default RecruitmentDetailPage;
